import type { Order, Tenant } from '@/types';
import type { WishlistService } from '../contracts';
import { affiliateSeed, demoUser, usedCouponCodes } from '@/data/account';
import { testimonials } from '@/data/marketing';
import { DEMO_TENANT_ID } from '@/data/adminSeed';
import { demoTenant } from '@/data/tenant';
import { couponDiscount, cartSubtotal } from '@/lib/pricing';
import {
  ServiceError, type AffiliateService, type AuthService, type CouponService, type LoyaltyService,
  type MarketingService, type OrderService, type TenantService, type WalletService,
} from '../contracts';
import { isStaffEmail, userForEmail } from '@/data/users';
import { db, delay, session, shouldFail, uid } from './db';
import { dataFor, hydrateCustomers, nowIso, saveAdmin, uid as adminUid } from './admin/context';

const guard = () => {
  if (shouldFail()) throw new ServiceError('Simulated service failure (?fail is set).');
};
const requireUser = () => {
  if (!db.user) throw new ServiceError('Please sign in to continue', 'UNAUTHORIZED');
  return db.user;
};

/** Storefront tenant = base defaults + whatever the merchant configured in the admin (settings, branding, theme, nav, modules). */
function tenantFromAdmin(): Tenant {
  const d = dataFor(DEMO_TENANT_ID);
  const a = d.store.address;
  return {
    ...demoTenant, name: d.store.name, tagline: d.store.tagline, announcement: d.store.announcement || undefined,
    contact: { email: d.store.email, phone: d.store.phone, address: `${a.line1}, ${a.city}, ${a.state} ${a.pincode}` },
    theme: { ...demoTenant.theme, primaryColor: d.branding.primaryColor, secondaryColor: d.branding.secondaryColor, accentColor: d.branding.accentColor, fontFamily: d.branding.fontFamily, borderRadius: d.theme.borderRadius, defaultMode: d.theme.defaultMode, allowModeToggle: d.theme.allowModeToggle },
    features: { ...d.features }, shipping: { freeThreshold: d.store.shipping.freeThreshold, flatFee: d.store.shipping.flatFee }, taxRate: d.store.tax.rate,
    seo: d.store.seo, logoUrl: d.branding.logoUrl, navigation: d.navigation,
    loyalty: { pointsPer100: d.loyaltyConfig.earn.pointsPer100, pointValue: d.loyaltyConfig.redeem.pointValue, minRedeem: d.loyaltyConfig.redeem.minRedeem },
  };
}
export const mockTenant: TenantService = { getCurrent: () => delay(tenantFromAdmin(), 150) };

export const mockMarketing: MarketingService = {
  async listBanners() {
    guard();
    const now = Date.now();
    const live = dataFor(DEMO_TENANT_ID).banners
      .filter((b) => b.enabled && (!b.startsAt || Date.parse(b.startsAt) <= now) && (!b.endsAt || Date.parse(b.endsAt) >= now))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return delay(live);
  },
  async listTestimonials() { return delay(testimonials); },
  async submitContact() { return delay({ ticketId: uid('TKT') }, 700); },
};

const liveCoupons = () => {
  const now = Date.now();
  return dataFor(DEMO_TENANT_ID).coupons.filter((c) => c.enabled && (!c.startsAt || Date.parse(c.startsAt) <= now));
};

export const mockCoupons: CouponService = {
  async listPublic() {
    guard();
    const now = Date.now();
    return delay(liveCoupons().filter((c) => Date.parse(c.expiresAt) > now && c.usedCount < c.usageLimit));
  },
  async listMine() {
    guard();
    const now = Date.now();
    const couponsSeed = liveCoupons();
    const reason = (c: (typeof couponsSeed)[number]) =>
      usedCouponCodes.includes(c.code) ? 'Used'
      : Date.parse(c.expiresAt) <= now ? 'Expired'
      : c.usedCount >= c.usageLimit ? 'Fully redeemed' : undefined;
    return delay({
      available: couponsSeed.filter((c) => !reason(c)),
      inactive: couponsSeed.flatMap((c) => { const r = reason(c); return r ? [{ coupon: c, reason: r }] : []; }),
    });
  },
  async validate(code, lines) {
    const coupon = liveCoupons().find((c) => c.code === code.trim().toUpperCase());
    const fail = (reason: string) => delay({ valid: false as const, reason }, 250);
    if (!coupon) return fail('This coupon code is not valid.');
    if (Date.parse(coupon.expiresAt) < Date.now()) return fail('This coupon has expired.');
    if (coupon.usedCount >= coupon.usageLimit || usedCouponCodes.includes(coupon.code)) return fail('This coupon is no longer available.');
    const subtotal = cartSubtotal(lines);
    if (subtotal < coupon.minOrder) return fail(`Add items worth ₹${coupon.minOrder - subtotal} more to use this coupon.`);
    return delay({ valid: true as const, coupon, discount: couponDiscount(coupon, subtotal) }, 250);
  },
};

/** MOCK AUTH: any email/password signs in as the demo user. Real JWT/refresh-token auth arrives with the backend. */
export const mockAuth: AuthService = {
  async currentUser() { return delay(db.user, 150); },
  async login({ email, password }) {
    if (!email || !password) throw new ServiceError('Enter your email and password', 'INVALID');
    if (password.length < 6) throw new ServiceError('Incorrect email or password', 'UNAUTHORIZED');
    db.user = userForEmail(email);
    session.write(email);
    return delay(db.user, 600);
  },
  async register({ name, email }) {
    if (isStaffEmail(email)) throw new ServiceError('An account with this email already exists', 'CONFLICT');
    db.user = { ...structuredClone(demoUser), name, email, memberSince: new Date().toISOString() };
    session.write(email);
    return delay(db.user, 700);
  },
  async logout() { db.user = null; session.write(null); return delay(undefined, 100); },
  async updateProfile(input) {
    const u = requireUser();
    db.user = { ...u, ...input };
    return delay(db.user, 500);
  },
  async listAddresses() { requireUser(); return delay(db.addresses); },
  async saveAddress(a) {
    requireUser();
    const saved = { ...a, id: a.id ?? uid('addr') };
    if (saved.isDefault) db.addresses.forEach((x) => (x.isDefault = false));
    const i = db.addresses.findIndex((x) => x.id === saved.id);
    if (i >= 0) db.addresses[i] = saved; else db.addresses.push({ ...saved, isDefault: saved.isDefault || db.addresses.length === 0 });
    db.save();
    return delay(saved, 400);
  },
  async removeAddress(id) {
    requireUser();
    db.addresses = db.addresses.filter((a) => a.id !== id);
    db.save();
    return delay(undefined, 300);
  },
};

/**
 * Cross-system consistency for the demo: an order placed on the storefront also lands in the merchant admin
 * (same tenant), with its stock movement and coupon usage. In the real system this is simply one database.
 */
function mirrorToAdmin(order: Order, walletApplied: number) {
  try {
    const d = dataFor(DEMO_TENANT_ID);
    const cust = hydrateCustomers(d)[0]; // demo shopper maps to the first seeded customer (Aarav Sharma)
    if (!cust) return;
    d.orders.push({
      ...structuredClone(order), tenantId: d.tenantId, customer: { id: cust.id, name: cust.name, email: cust.email, phone: cust.phone }, notes: [],
    });
    const now = nowIso();
    for (const it of order.items) {
      const p = d.products.find((x) => x.id === it.productId);
      if (!p) continue;
      const qty = Math.min(it.quantity, p.stock);
      if (qty <= 0) continue;
      d.inventory.push({ id: adminUid('inv'), tenantId: d.tenantId, productId: p.id, productName: p.name, sku: p.sku, delta: -qty, before: p.stock, after: p.stock - qty, reason: 'SALE', note: order.number, actorName: 'Storefront', createdAt: now });
      p.stock -= qty;
      if (p.variants.length) { let left = qty; for (const v of p.variants) { const t = Math.min(v.stock, left); v.stock -= t; left -= t; if (!left) break; } }
    }
    if (order.couponCode) { const c = d.coupons.find((x) => x.code === order.couponCode); if (c) c.usedCount++; }
    if (walletApplied > 0) {
      const bal = d.wallet.filter((w) => w.customerId === cust.id).reduce((s, w) => s + (w.direction === 'CREDIT' ? w.amount : -w.amount), 0);
      if (bal >= walletApplied) d.wallet.push({ id: adminUid('wtx'), tenantId: d.tenantId, customerId: cust.id, customerName: cust.name, type: 'ORDER_PAYMENT', direction: 'DEBIT', amount: walletApplied, balanceAfter: bal - walletApplied, reference: order.number, createdAt: now });
    }
    saveAdmin();
  } catch { /* admin data unavailable: storefront order still succeeds */ }
}

export const mockOrders: OrderService = {
  async list() { guard(); requireUser(); return delay(db.orders); },
  async get(id) {
    guard(); requireUser();
    const o = db.orders.find((x) => x.id === id || x.number === id);
    if (!o) throw new ServiceError('Order not found', 'NOT_FOUND');
    return delay(o);
  },
  async placeGuest(input) {
    const now = new Date().toISOString();
    const order: Order = {
      id: uid('ord'),
      number: `MCE-${100413 + db.orders.length}`,
      status: input.paymentMethod === 'COD' ? 'PENDING' : 'PAID',
      placedAt: now,
      items: input.lines.map((l) => ({ productId: l.productId, slug: l.productId, name: 'Guest item', price: 0, quantity: l.quantity, icon: 'package', hue: 200 })),
      pricing: { subtotal: 0, couponDiscount: 0, walletApplied: 0, pointsApplied: 0, shipping: 0, taxIncluded: 0, total: 0, payable: 0 },
      paymentMethod: input.paymentMethod,
      paymentStatus: input.paymentMethod === 'COD' ? 'PENDING' : 'PAID',
      address: { id: 'guest', label: 'Guest', name: input.name, phone: input.phone, line1: input.address.line1, city: input.address.city, state: input.address.state, pincode: input.address.pincode, isDefault: true },
      timeline: [{ status: 'PENDING', at: now, note: 'Guest checkout' }],
    };
    db.orders.unshift(order);
    return delay(order);
  },
  async place(input) {
    requireUser();
    const now = new Date().toISOString();
    const paid = input.paymentMethod !== 'COD' && input.pricing.payable >= 0;
    const order: Order = {
      id: uid('ord'),
      number: `MCE-${100413 + db.orders.length}`,
      status: paid ? 'PAID' : 'PENDING',
      placedAt: now,
      items: input.lines.filter((l) => !l.savedForLater).map((l) => ({
        productId: l.productId, slug: l.slug, name: l.name, price: l.price, quantity: l.quantity, icon: l.icon, hue: l.hue, variant: l.variant,
      })),
      pricing: input.pricing,
      paymentMethod: input.paymentMethod,
      paymentStatus: paid ? 'PAID' : 'PENDING',
      address: input.address,
      couponCode: input.couponCode,
      timeline: [
        { status: 'PENDING', at: now, note: 'Order placed' },
        ...(paid ? [{ status: 'PAID' as const, at: now, note: '[Mock] Payment simulated — no real charge' }] : []),
      ],
    };
    db.orders.unshift(order);
    // Simulated ledger effects (server-authoritative in the real system).
    if (input.pricing.walletApplied > 0) {
      db.wallet.balance -= input.pricing.walletApplied;
      db.wallet.tx.unshift({ id: uid('wtx'), type: 'ORDER_PAYMENT', amount: -input.pricing.walletApplied, balanceAfter: db.wallet.balance, description: `Order payment — #${order.number}`, createdAt: now });
    }
    if (input.pricing.pointsApplied > 0) {
      db.loyalty.account.balance -= input.pricing.pointsApplied;
      db.loyalty.transactions.unshift({ id: uid('ltx'), type: 'REDEEM', points: -input.pricing.pointsApplied, description: `Redeemed at checkout — #${order.number}`, createdAt: now });
    }
    db.save();
    mirrorToAdmin(order, input.pricing.walletApplied);
    return delay(order, 900);
  },
  async requestReturn() {},
  async cancel(id) {
    const o = db.orders.find((x) => x.id === id);
    if (!o) throw new ServiceError('Order not found', 'NOT_FOUND');
    if (!['PENDING', 'CONFIRMED', 'PAID', 'PROCESSING'].includes(o.status)) throw new ServiceError('This order can no longer be cancelled', 'CONFLICT');
    o.status = 'CANCELLED';
    o.timeline.push({ status: 'CANCELLED', at: new Date().toISOString(), note: 'Cancelled by customer' });
    db.save();
    return delay(o, 500);
  },
};

export const mockWallet: WalletService = {
  async get() { guard(); requireUser(); return delay({ balance: db.wallet.balance, currency: 'INR' as const }); },
  async transactions() { guard(); requireUser(); return delay(db.wallet.tx); },
  async topUp(amount) {
    requireUser();
    if (!(amount >= 100 && amount <= 50000)) throw new ServiceError('Enter an amount between ₹100 and ₹50,000', 'INVALID');
    db.wallet.balance += amount;
    db.wallet.tx.unshift({ id: uid('wtx'), type: 'TOP_UP', amount, balanceAfter: db.wallet.balance, description: 'Wallet top-up (mock gateway)', createdAt: new Date().toISOString() });
    db.save();
    return delay({ balance: db.wallet.balance, currency: 'INR' as const }, 900);
  },
};

export const mockLoyalty: LoyaltyService = {
  async get() { guard(); requireUser(); return delay(db.loyalty); },
  async redeemReward(rewardId) {
    requireUser();
    const r = db.loyalty.account.rewards.find((x) => x.id === rewardId);
    if (!r) throw new ServiceError('Reward not found', 'NOT_FOUND');
    if (db.loyalty.account.balance < r.cost) throw new ServiceError('Not enough points for this reward', 'CONFLICT');
    db.loyalty.account.balance -= r.cost;
    db.loyalty.account.lifetimeRedeemed += r.cost;
    db.loyalty.transactions.unshift({ id: uid('ltx'), type: 'REDEEM', points: -r.cost, description: `Reward: ${r.title}`, createdAt: new Date().toISOString() });
    db.save();
    return delay(db.loyalty.account, 500);
  },
};

export const mockAffiliate: AffiliateService = {
  async summary() { guard(); requireUser(); return delay(affiliateSeed); },
};

export const mockWishlist: WishlistService = {
  list: async () => [],
  listIds: async () => [],
  toggle: async (productId: string) => ({ inWishlist: true, productId }),
};
